import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Form, InputGroup, ListGroup, Spinner, Button } from "react-bootstrap";
import DataApi from "../../api/DataApi";

function isUuidString(s) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(s || "").trim()
  );
}

/** Single form field value → string for lookup filter API (lookup id, multipicklist, boolean, etc.). */
function coerceFormFieldValueForLookupFilter(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "object" && v !== null && "id" in v) {
    const id = v.id;
    return id === null || id === undefined ? "" : String(id).trim();
  }
  if (Array.isArray(v)) {
    return v.map((x) => String(x).trim()).filter(Boolean).join(",");
  }
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v).trim();
}

/**
 * Replace {{fieldName}} in filter values with current form field values (same object / row).
 * A value that is only `{{field}}` becomes JSON null when that field is null/undefined (API applies IS NULL for equals on lookups).
 */
function resolveLookupFilterTemplates(filters, fieldValues) {
  if (!filters || !Array.isArray(filters) || filters.length === 0) return filters;
  if (!fieldValues || typeof fieldValues !== "object") return filters;
  return filters.map((f) => {
    if (!f || typeof f !== "object") return f;
    const raw = f.value;
    if (raw === undefined) return { ...f, value: null };
    if (raw === null) return { ...f, value: null };

    const s = String(raw);
    const onlyPlaceholder = s.match(/^\{\{([a-zA-Z0-9_]+)\}\}$/);
    if (onlyPlaceholder) {
      const name = onlyPlaceholder[1];
      const fv = fieldValues[name];
      if (fv === null || fv === undefined) {
        return { ...f, value: null };
      }
      return { ...f, value: coerceFormFieldValueForLookupFilter(fv) };
    }

    const resolved = s.replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, (_, name) =>
      coerceFormFieldValueForLookupFilter(fieldValues[name])
    );
    return { ...f, value: resolved };
  });
}

/** Flatten object/view API payload (sections + hidden fields) for label resolution. */
function flattenViewRecordPayload(data) {
  if (!data || typeof data !== "object") return {};
  const out = {};
  const take = (fields) => {
    if (!Array.isArray(fields)) return;
    fields.forEach((f) => {
      if (!f) return;
      const name = f.fieldName || f.field_name;
      if (!name) return;
      const v = f.value !== undefined && f.value !== null ? f.value : f.displayValue;
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        out[name] = v;
      }
    });
  };
  if (Array.isArray(data.details)) {
    data.details.forEach((section) => take(section?.fields));
  }
  take(data.otherHiddenFields);
  return out;
}

const LookupLayout = ({
  objectName,
  searchFields,
  displayFields,
  value,
  defaultLabel,
  onChange,
  disabled,
  placeholder = "Search...",
  isRequired = false,
  lookupFilter = null, // Array of filter objects: [{field, operator, value}]
  lookupFilterLogic = 'AND', // AND or OR logic for combining filters
  /** When set, filter `value` strings may use {{fieldName}} placeholders resolved from this map (e.g. edit form state). */
  lookupFilterFieldValues = null,
  /** When lookup target is `recordview`, restrict rows to this CRM object (e.g. account on Account layout). */
  scopeRecordviewToObjectName = null,
  /** When set (non-empty), lookup suggestions for `recordview` are limited to these ids (role.permission_config). */
  recordviewIdAllowlist = null,
  /** Optional class on the root wrapper (e.g. align height with adjacent Form.Select). */
  rootClassName = "",
}) => {


  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(() => {
    if (!value || !defaultLabel) return null;
    const lt = String(defaultLabel).trim();
    const vt = String(value).trim();
    if (isUuidString(lt) && lt === vt) return null;
    return { id: value, label: defaultLabel };
  });
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const dropdownIdRef = useRef(`lookup-dropdown-${objectName}-${Date.now()}-${Math.random()}`);

  const searchKeys = searchFields?.split(",").map((f) => f.trim()) || [];
  const displayKeys = displayFields
    ? displayFields.split(",").map((f) => f.trim())
    : searchKeys;

  // Update selected state when value or defaultLabel props change
  useEffect(() => {
    if (value && defaultLabel) {
      const labelStr = String(defaultLabel).trim();
      const valueStr = String(value).trim();
      if (isUuidString(labelStr) && labelStr === valueStr) {
        setSelected(null);
      } else {
        setSelected({ id: value, label: defaultLabel });
      }
    } else if (!value) {
      setSelected(null);
    }
  }, [value, defaultLabel]);

  // When we have an id but no display label, load one record via view API (edit / hydration).
  useEffect(() => {
    const v = value && String(value).trim();
    if (!v || !objectName) return;
    const hasLabel = defaultLabel != null && String(defaultLabel).trim() !== "";
    if (hasLabel) return;

    let cancelled = false;
    (async () => {
      try {
        const dataApi = new DataApi(`object/view/${objectName}`);
        const res = await dataApi.fetchById(v);
        const payload = res?.data?.data;
        if (cancelled || !payload) return;
        const flat = flattenViewRecordPayload(payload);
        const label = displayKeys
          .map((key) => (flat[key] != null ? String(flat[key]).trim() : ""))
          .filter(Boolean)
          .join(" ");
        if (label) {
          setSelected({ id: v, label });
        }
      } catch {
        /* permission / missing record — leave unset */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [value, defaultLabel, objectName, displayFields]);

  // Calculate dropdown position when it should be shown
  useEffect(() => {
    if (showDropdown && suggestions.length > 0 && inputRef.current) {
      const updatePosition = () => {
        if (inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom,
            left: rect.left,
            width: rect.width,
          });
        }
      };

      updatePosition();

      // Update position on scroll or resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showDropdown, suggestions.length]);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        // Check if click is on the dropdown portal
        const dropdownElement = document.getElementById(dropdownIdRef.current);
        if (!dropdownElement || !dropdownElement.contains(event.target)) {
          setShowDropdown(false);
        }
      }
    };

    // Use a small delay to ensure the portal is rendered
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length < 2 || selected) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      try {
        setLoading(true);
        const dataApi = new DataApi(`object/lookup/${objectName}`);

        // Build search params
        const searchParams = {
          q: searchTerm,
          fields: searchFields,
          display_fields: displayFields,
        };

        // Convert lookup_filter array to filters array format with logic (optional {{field}} placeholders)
        let filtersArray = [];
        const resolvedLookupFilter = resolveLookupFilterTemplates(
          lookupFilter,
          lookupFilterFieldValues
        );
        if (
          resolvedLookupFilter &&
          Array.isArray(resolvedLookupFilter) &&
          resolvedLookupFilter.length > 0
        ) {
          // Include null and empty-string values so the API applies IS NULL / blank semantics.
          filtersArray = resolvedLookupFilter
            .filter(filter => filter.field && filter.operator && filter.value !== undefined)
            .map(filter => {
              // Handle operators that support multiple values (in, not_in)
              let filterValue = filter.value;
              if (filter.operator === 'in' || filter.operator === 'not_in') {
                if (filter.value == null) {
                  filterValue = filter.value;
                } else {
                  const values = String(filter.value)
                    .split(',')
                    .map(v => v.trim())
                    .filter(Boolean);
                  filterValue = values.length === 1 ? values[0] : values;
                }
              }
              return {
                field: filter.field,
                operator: filter.operator,
                value: filterValue
              };
            });
        }

        // Add filter for active roles only when objectName is 'role' (if no custom filters)
        if (filtersArray.length === 0 && objectName === 'role') {
          filtersArray = [{ field: 'is_active', operator: 'equals', value: 'true' }];
        }

        let effectiveFilterLogic =
          lookupFilterLogic && (lookupFilterLogic.toUpperCase() === 'OR' || lookupFilterLogic.toUpperCase() === 'AND')
            ? lookupFilterLogic.toUpperCase()
            : 'AND';

        const lookupTarget = String(objectName || '').toLowerCase().trim();
        const rvScope =
          scopeRecordviewToObjectName && String(scopeRecordviewToObjectName).trim();
        if (lookupTarget === 'recordview' && rvScope) {
          searchParams.recordviewForObject = rvScope;
        }

        // Add filters to search params if any exist
        if (filtersArray.length > 0) {
          searchParams.filters = JSON.stringify(filtersArray);
          searchParams.filterLogic = effectiveFilterLogic;
        }

        const res = await dataApi.search(searchParams);

        let rows = Array.isArray(res.data) ? res.data : [];
        const lookupTargetLc = String(objectName || '').toLowerCase().trim();
        const rvScopeEff =
          scopeRecordviewToObjectName && String(scopeRecordviewToObjectName).trim();
        if (
          lookupTargetLc === 'recordview' &&
          rvScopeEff &&
          Array.isArray(recordviewIdAllowlist) &&
          recordviewIdAllowlist.length > 0
        ) {
          const allow = new Set(recordviewIdAllowlist.map((x) => String(x)));
          rows = rows.filter((item) => allow.has(String(item.id)));
        }

        const dataWithLabels = rows.map((item) => ({
          ...item,
          label: displayKeys
            .map((key) => item[key]?.trim?.() || "")
            .filter(Boolean)
            .join(" "),
        }));

        setSuggestions(dataWithLabels);
      } catch (err) {
        console.error("Lookup error", err);
      } finally {
        setLoading(false);
        setShowDropdown(true);
      }
    };

    const delayDebounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, objectName, searchFields, displayFields, lookupFilter, lookupFilterLogic, lookupFilterFieldValues, scopeRecordviewToObjectName, recordviewIdAllowlist]);

  const handleSelect = (item) => {
    setSelected(item);
    setSearchTerm("");
    setShowDropdown(false);
    onChange(item.id, item.label);
  };

  const handleClear = () => {
    setSelected(null);
    onChange(null, "");
  };

  return (
    <div
      style={{ position: "relative" }}
      ref={inputRef}
      className={rootClassName || undefined}
    >
      <InputGroup>
        {selected ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f0f0f0",
              borderRadius: "4px",
              padding: "6px 12px",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {selected.label}
            </span>
            <Button
              variant="link"
              size="sm"
              onClick={handleClear}
              style={{
                color: "#000",
                textDecoration: "none",
                fontSize: "large",
                padding: "0px",
                margin: "0px",
              }}
              disabled={disabled}
            >
              ×
            </Button>
          </div>
        ) : (
          <>
            {value ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  minHeight: "40px",
                  backgroundColor: "#f8f9fa",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ flex: 1, fontWeight: 500 }}>{searchTerm}</span>

                <span
                  onClick={() => {
                    onChange(null, "");
                    setSearchTerm("");
                    setSuggestions([]);
                  }}
                  style={{
                    cursor: "pointer",
                    fontSize: "15px",
                    fontWeight: "bold",
                    color: "#dc3545",
                    padding: "0 6px",
                    lineHeight: 1,
                    userSelect: "none",
                  }}
                  title="Clear"
                >
                  &#10005; {/* Unicode for '×' cross */}
                </span>
              </div>
            ) : (
              <Form.Control
                type="text"
                value={searchTerm}
                placeholder={placeholder}
                disabled={disabled}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => {
                  if (searchTerm.length >= 2) setShowDropdown(true);
                }}
                required={isRequired}
              />
            )}

            {loading && (
              <InputGroup.Text>
                <Spinner animation="border" size="sm" />
              </InputGroup.Text>
            )}
          </>
        )}
      </InputGroup>

      {showDropdown && suggestions.length > 0 && !selected && typeof document !== 'undefined' && createPortal(
        <ListGroup
          id={dropdownIdRef.current}
          style={{
            position: "fixed",
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 9999,
            maxHeight: 200,
            overflowY: "auto",
            background: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            borderRadius: "4px",
            border: "1px solid #ccc",
            marginTop: "2px",
          }}
        >
          {suggestions.map((item) => (
            <ListGroup.Item
              action
              key={item.id}
              onClick={() => handleSelect(item)}
              style={{ cursor: "pointer" }}
            >
              {item.label}
            </ListGroup.Item>
          ))}
        </ListGroup>,
        document.body
      )}
    </div>
  );
};

export default LookupLayout;
